import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import {
  clampSidebarSectionListHeight,
  measureSidebarSectionListMetrics,
  snapSidebarSectionListHeight,
} from '../sidebar-expandable-section/list-snap';

const DEFAULT_VISIBLE_ITEMS = 7;
const MINIMUM_VISIBLE_ITEMS = 1;
const MINIMUM_EXPANDED_CONTENT_HEIGHT = 44;
const DEFAULT_SNAP_TARGET_SELECTOR =
  '[data-sidebar-section-snap-target="true"]';

const measuredMetrics = (element: HTMLElement | null, targetSelector: string) =>
  measureSidebarSectionListMetrics(element, {
    targetSelector,
  });

const measuredContentHeight = (
  element: HTMLElement | null,
  targetSelector: string
) => {
  const snapPoints =
    measuredMetrics(element, targetSelector)?.heightSnapPoints ?? [];
  return snapPoints[snapPoints.length - 1] ?? 0;
};

const clampDragHeight = (height: number) => Math.max(0, height);

const defaultMeasuredHeight = (
  element: HTMLElement | null,
  targetSelector: string,
  visibleItems: number
) => {
  const snapPoints = measuredMetrics(element, targetSelector)?.heightSnapPoints;
  if (!snapPoints?.length) return 0;

  return snapPoints[Math.min(snapPoints.length, visibleItems) - 1];
};

/**
 * Keeps the boundary between personal and team areas aligned to the actual
 * rendered rows. Consumers may use different row chrome without duplicating
 * row-height constants or resize behavior.
 */
export function useWorkspaceAppSidebarAreaResize({
  defaultVisibleItems = DEFAULT_VISIBLE_ITEMS,
  expanded,
  itemCount,
  listRef,
  minimumVisibleItems = MINIMUM_VISIBLE_ITEMS,
  onPreferredContentHeightCommit,
  preferredContentHeight,
  snapTargetSelector = DEFAULT_SNAP_TARGET_SELECTOR,
}: {
  defaultVisibleItems?: number;
  expanded: boolean;
  itemCount: number;
  listRef: RefObject<HTMLDivElement | null>;
  minimumVisibleItems?: number;
  onPreferredContentHeightCommit?: (height: number) => void;
  preferredContentHeight?: number | null;
  snapTargetSelector?: string;
}) {
  const initialPreferredContentHeight =
    typeof preferredContentHeight === 'number' &&
    Number.isFinite(preferredContentHeight) &&
    preferredContentHeight >= 0
      ? preferredContentHeight
      : null;
  const initializedRef = useRef(false);
  const hasPreferredContentHeightRef = useRef(
    initialPreferredContentHeight !== null
  );
  const preferredContentHeightRef = useRef(initialPreferredContentHeight ?? 0);
  const persistedContentHeightRef = useRef<number | null>(
    initialPreferredContentHeight
  );
  const resizingRef = useRef(false);
  const [contentHeight, setContentHeight] = useState(0);

  const resizeOptions = useCallback(() => {
    const measuredItemCount =
      measuredMetrics(listRef.current, snapTargetSelector)?.heightSnapPoints
        .length ?? itemCount;
    return {
      clamp: clampDragHeight,
      fallbackMinHeight: MINIMUM_EXPANDED_CONTENT_HEIGHT,
      minimumVisibleItems: Math.min(minimumVisibleItems, measuredItemCount),
      targetSelector: snapTargetSelector,
    };
  }, [itemCount, listRef, minimumVisibleItems, snapTargetSelector]);

  const onContentHeightChange = useCallback(
    (height: number) => {
      preferredContentHeightRef.current = height;
      setContentHeight(
        clampSidebarSectionListHeight(height, listRef.current, resizeOptions())
      );
    },
    [listRef, resizeOptions]
  );

  const onContentHeightCommit = useCallback(
    (height: number) => {
      if (measuredContentHeight(listRef.current, snapTargetSelector) === 0) {
        const minimumHeight = Math.max(MINIMUM_EXPANDED_CONTENT_HEIGHT, height);
        preferredContentHeightRef.current = minimumHeight;
        hasPreferredContentHeightRef.current = true;
        setContentHeight(minimumHeight);
        onPreferredContentHeightCommit?.(minimumHeight);
        return;
      }
      const snappedHeight = snapSidebarSectionListHeight(
        height,
        listRef.current,
        resizeOptions()
      );
      preferredContentHeightRef.current = snappedHeight;
      hasPreferredContentHeightRef.current = true;
      setContentHeight(snappedHeight);
      onPreferredContentHeightCommit?.(snappedHeight);
    },
    [listRef, onPreferredContentHeightCommit, resizeOptions, snapTargetSelector]
  );

  const onResizeActiveChange = useCallback((active: boolean) => {
    resizingRef.current = active;
  }, []);

  const reconcileMeasuredHeight = useCallback(() => {
    if (!expanded || resizingRef.current) return;
    if (measuredContentHeight(listRef.current, snapTargetSelector) === 0) {
      initializedRef.current = false;
      setContentHeight(MINIMUM_EXPANDED_CONTENT_HEIGHT);
      return;
    }

    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!hasPreferredContentHeightRef.current) {
        preferredContentHeightRef.current = defaultMeasuredHeight(
          listRef.current,
          snapTargetSelector,
          defaultVisibleItems
        );
        hasPreferredContentHeightRef.current = true;
      }
      setContentHeight(
        snapSidebarSectionListHeight(
          preferredContentHeightRef.current,
          listRef.current,
          resizeOptions()
        )
      );
      return;
    }

    setContentHeight(
      snapSidebarSectionListHeight(
        preferredContentHeightRef.current,
        listRef.current,
        resizeOptions()
      )
    );
  }, [
    defaultVisibleItems,
    expanded,
    listRef,
    resizeOptions,
    snapTargetSelector,
  ]);

  useLayoutEffect(() => {
    reconcileMeasuredHeight();
  }, [itemCount, reconcileMeasuredHeight]);

  useLayoutEffect(() => {
    const nextPreferredContentHeight =
      typeof preferredContentHeight === 'number' &&
      Number.isFinite(preferredContentHeight) &&
      preferredContentHeight >= 0
        ? preferredContentHeight
        : null;
    if (nextPreferredContentHeight === persistedContentHeightRef.current) {
      return;
    }

    persistedContentHeightRef.current = nextPreferredContentHeight;
    hasPreferredContentHeightRef.current = nextPreferredContentHeight !== null;
    preferredContentHeightRef.current = nextPreferredContentHeight ?? 0;
    initializedRef.current = false;
    reconcileMeasuredHeight();
  }, [preferredContentHeight, reconcileMeasuredHeight]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;

    let frame = 0;
    const scheduleMeasurement = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(reconcileMeasuredHeight);
    };
    const resizeObserver = new ResizeObserver(scheduleMeasurement);
    const observeSnapTargets = () => {
      resizeObserver.disconnect();
      const markedTargets = Array.from(
        list.querySelectorAll<HTMLElement>(snapTargetSelector)
      );
      const targets = markedTargets.length
        ? markedTargets
        : Array.from(list.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement
          );
      targets.forEach(target => resizeObserver.observe(target));
    };
    const mutationObserver = new MutationObserver(() => {
      observeSnapTargets();
      scheduleMeasurement();
    });

    observeSnapTargets();
    mutationObserver.observe(list, {
      attributeFilter: ['aria-hidden', 'data-collapsed', 'inert'],
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [listRef, reconcileMeasuredHeight, snapTargetSelector]);

  return {
    contentHeight,
    onContentHeightChange,
    onContentHeightCommit,
    onResizeActiveChange,
  };
}
